/**
 * FILTER UX V2 (2026-07-08) — variant-B behavior for the Quince-style filter revamp A/B test.
 *
 * Loaded ONLY on v2 templates (gated in snippets/boost-pfs.liquid, between
 * boost-pfs-filter.js and boost-pfs-init.js — deferred scripts execute in document order,
 * so the boostPFSFilterConfig mutations and prototype wraps below land before
 * boost-pfs-init.js instantiates BoostPFS). Control sessions never load this file.
 *
 * Boost hooks used (verified in assets/boost-pfs-core.js / boost-pfs-filter.js):
 *   - boostPFSFilterConfig.general/.label merge into Settings before init
 *   - Filter.prototype.afterRender(data) — theme-owned, race-free post-render hook
 *   - FilterApi.setParam / FilterApi.applyFilter — programmatic filter mutation
 *   - .boost-pfs-filter-tree-mobile-open class on #boost-pfs-filter-tree (sheet state)
 *   - .boost-pfs-filter-show-result footer button (closes the tree, live count)
 */
(function () {
  'use strict';

  if (!document.querySelector('.mt-fv2')) return;
  if (typeof window.boostPFSFilterConfig === 'undefined') return;

  // Every visible facet becomes a pill in the swipeable rail (Quince-style — no cap).

  /* ---------- 1. Config overrides — MUST run before boost-pfs-init.js ---------- */
  boostPFSFilterConfig.general = boostPFSFilterConfig.general || {};
  // v2 owns stickiness in CSS (mt-filter-v2.css); Boost's JS sticky would fight it.
  boostPFSFilterConfig.general.stickyFilterOnMobile = false;
  boostPFSFilterConfig.general.stickyFilterOnDesktop = false;
  // Boost lazy-renders facet values beyond the first 24 only when its inner scrollbox
  // scrolls; the sheet removed that scrollbox (single scroller) and iOS never fired it
  // anyway — render every value up front (longest facet is ~170 values).
  boostPFSFilterConfig.general.scrollFirstLoadLength = 999;
  // Boost culls any facet with exactly one value when showSingleOption is TRUE — the
  // vendor's naming is inverted vs. its isRender() logic (`t ? e>1 : e>0` in core).
  // That hid boolean facets like Personalizable ("yes", 78 products on jewish_jewelry).
  // FALSE renders every active facet with >=1 value (2026-09-14).
  boostPFSFilterConfig.general.showSingleOption = false;
  // DESKTOP GRID 2026 topbar (2026-08-05): Quince applies filters the moment a value is
  // clicked — no Apply button. requestInstantly only affects the horizontal dropdown
  // panels (vertical trees already instant-apply), and the H tree exists only on topbar
  // variant templates, so this is variant-scoped by construction; guarded anyway.
  if (document.querySelector('.mt-dgrid-topbar')) {
    boostPFSFilterConfig.general.requestInstantly = true;
  }
  boostPFSFilterConfig.label = boostPFSFilterConfig.label || {};
  boostPFSFilterConfig.label.refineMobile = 'Filter'; // Quince parity — icon carries "all"
  boostPFSFilterConfig.label.refine = 'Filters'; // sheet header title
  boostPFSFilterConfig.label.showResult = 'Show results';
  boostPFSFilterConfig.label.clearAll = 'Clear all';

  /* ---------- helpers ---------- */
  function track(name, params) {
    try {
      params = params || {};
      params.mt_variant = 'b';
      if (typeof window.gtag === 'function') window.gtag('event', name, params);
    } catch (e) { /* analytics must never break the UI */ }
  }

  function escapeHtml(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function toArray(v) {
    if (v === null || typeof v === 'undefined' || v === '') return [];
    return Array.isArray(v) ? v.slice() : [v];
  }

  function getQueryParams() {
    try {
      if (typeof window.Globals !== 'undefined' && window.Globals.queryParams) {
        return window.Globals.queryParams;
      }
    } catch (e) { /* fall through */ }
    return {};
  }

  // "material:silver" -> "Silver" · "10:25" on a price facet -> "$10 – $25" · "25:" -> "Over $25"
  function formatValue(key, raw) {
    var v = String(raw);
    var range = v.match(/^(\d*\.?\d*):(\d*\.?\d*)$/);
    if (range) {
      var money = /price/i.test(key) ? '$' : '';
      var lo = range[1], hi = range[2];
      if (/sale|percent/i.test(key)) return 'On Sale';
      if (lo && hi) return money + lo + ' – ' + money + hi;
      if (lo) return 'Over ' + money + lo;
      if (hi) return 'Under ' + money + hi;
      return v;
    }
    if (v.indexOf(':') > 0) v = v.slice(v.indexOf(':') + 1);
    v = v.replace(/[-_]+/g, ' ').trim();
    return v.replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  // Boost renders each facet group with a slug class, e.g. "boost-pfs-filter-option-material".
  // Skip the structural/display classes to isolate the slug.
  var OPTION_STRUCTURAL = /^(list|box|swatch|range|rating|hidden|collapsed|non-collapse|show-search-box|open-list|close-list|content.*|title.*|label|item.*|column-\d+|amount-\d+|multi-level-tag|single-list|multiple-list)$/;
  function optionSlugFromClasses(el) {
    var classes = (el.className || '').split(/\s+/);
    for (var i = 0; i < classes.length; i++) {
      var m = classes[i].match(/^boost-pfs-filter-option-(.+)$/);
      if (m && !OPTION_STRUCTURAL.test(m[1])) return m[1];
    }
    return '';
  }

  // Query params use prefixed keys (pf_t_material, pf_p_price…); map slug -> actual key.
  function paramKeyForSlug(slug) {
    if (!slug) return '';
    var params = getQueryParams();
    var keys = Object.keys(params);
    for (var i = 0; i < keys.length; i++) {
      if (keys[i].indexOf('pf_') === 0 && keys[i].replace(/^pf_[a-z]+_/, '') === slug) return keys[i];
    }
    return '';
  }

  /* ---------- facet search-within (2026-09-15, built+live-verified by subagent) ----------
     Long facets (>20 values — Artist runs 55-275) get a type-to-filter input above the
     value list. Boost wipes each tree's innerHTML on every render, so the input is
     re-injected from onBoostRender (afterRender pass) and the typed query survives in a
     closure Map keyed by facet label. */
  var mtFacetSearchState = new Map();

  function mtFacetLabel(group) {
    var titleEl = group.querySelector('.boost-pfs-filter-option-title-text')
      || group.querySelector('.boost-pfs-filter-option-title-heading')
      || group.querySelector('.boost-pfs-filter-option-title');
    if (!titleEl) return '';
    var textNode = titleEl.childNodes[0];
    var label = (textNode && textNode.nodeType === 3 ? textNode.nodeValue : titleEl.textContent) || '';
    return label.replace(/\s+/g, ' ').replace(/\(\d+\)/g, '').trim();
  }

  /* case-insensitive substring; a CHECKED row is never hidden (li.selected /
     aria-checked=true — verified against live markup) */
  function mtFilterFacetRows(list, query) {
    var q = String(query || '').trim().toLowerCase();
    var rows = list.querySelectorAll(':scope > li');
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var checked = row.classList.contains('selected') || !!row.querySelector('[aria-checked="true"]');
      if (checked || !q) { row.style.display = ''; continue; }
      var text = (row.textContent || '').toLowerCase();
      row.style.display = text.indexOf(q) === -1 ? 'none' : '';
    }
  }

  function mtWireFacetGroup(group) {
    var content = group.querySelector('.boost-pfs-filter-option-content');
    if (!content) return;
    var inner = content.querySelector('.boost-pfs-filter-option-content-inner') || content;
    var list = inner.querySelector('ul');
    if (!list) return;
    var rows = list.querySelectorAll(':scope > li');
    if (rows.length <= 20) return;
    var label = mtFacetLabel(group);
    var input = inner.querySelector(':scope > input.mt-facet-search');
    if (!input) {
      input = document.createElement('input');
      input.type = 'search';
      input.setAttribute('inputmode', 'search');
      input.placeholder = 'Search';
      input.className = 'mt-facet-search';
      input.setAttribute('autocomplete', 'off');
      input.setAttribute('aria-label', 'Search ' + (label || 'facet') + ' options');
      inner.insertBefore(input, list);
      input.addEventListener('click', function (e) { e.stopPropagation(); });
      input.addEventListener('keydown', function (e) {
        /* Escape with a query clears it; Escape on an EMPTY box is left to bubble so
           the document-level handler can close the dropdown/panel. Every other key is
           stopped so typing is never misread by outside-click/toggle handlers. */
        if (e.key === 'Escape') {
          if (input.value) {
            e.preventDefault();
            e.stopPropagation();
            input.value = '';
            mtFacetSearchState.set(label, '');
            mtFilterFacetRows(list, '');
          }
          return;
        }
        e.stopPropagation();
      });
      input.addEventListener('input', function () {
        mtFacetSearchState.set(label, input.value);
        mtFilterFacetRows(list, input.value);
      });
    }
    var savedQuery = mtFacetSearchState.get(label) || '';
    if (input.value !== savedQuery) input.value = savedQuery;
    mtFilterFacetRows(list, savedQuery);
  }

  /* one document-wide sweep covers all three Boost trees (H bar, All Filters panel,
     mobile sheet) */
  function mtAddFacetSearch(root) {
    root = root || document;
    var groups = root.querySelectorAll('.boost-pfs-filter-option');
    for (var i = 0; i < groups.length; i++) {
      mtWireFacetGroup(groups[i]);
    }
  }

  /* ---------- facet label parity (2026-09-15, built+live-verified by subagent) ----------
     The Jewelry tree's facet renders as "Made In" while every other tree and the Blink
     pages say "Origin". Display-level map only: every place that MATCHES a facet does so
     by CSS class/slug (optionSlugFromClasses, buildPills' data-mt-option, openSheet's
     class lookup), never by label text — so mutating the title TEXT NODE (plus the two
     Boost aria-labels echoing it) is safe by construction. MUST run before buildPills()
     each render: the mobile pill rail reads title text off the DOM, so it inherits the
     mapped label for free. */
  var MT_LABEL_MAP = { 'Made In': 'Origin' };

  function mtRelabelFacets(root) {
    root = root || document;
    var groups = root.querySelectorAll('.boost-pfs-filter-option');
    for (var i = 0; i < groups.length; i++) {
      var g = groups[i];
      var titleEl = g.querySelector('.boost-pfs-filter-option-title-text');
      if (!titleEl) continue;
      var textNode = titleEl.childNodes[0]; /* the count span is a LATER sibling */
      if (!textNode || textNode.nodeType !== 3) continue;
      var current = textNode.nodeValue.replace(/\s+/g, ' ').trim();
      var mapped = MT_LABEL_MAP[current];
      if (!mapped) continue;
      textNode.nodeValue = mapped;
      var heading = g.querySelector('.boost-pfs-filter-option-title-heading');
      if (heading && heading.getAttribute('aria-label') === 'Filter by ' + current) {
        heading.setAttribute('aria-label', 'Filter by ' + mapped);
      }
      var clearBtn = g.querySelector('.boost-pfs-filter-clear');
      if (clearBtn && clearBtn.getAttribute('aria-label') === 'Clear filter by ' + current) {
        clearBtn.setAttribute('aria-label', 'Clear filter by ' + mapped);
      }
    }
    mtWatchMobileSheetTitle();
  }

  /* The mobile sheet's full-screen sub-panel sets its own title on a pure UI toggle with
     NO afterRender pass in between (caught live: pill said "Origin", sub-panel title said
     "MADE IN") — a MutationObserver on the sheet root is the only handle that survives
     regardless of when Boost sets that text. Armed once; safe no-op if the root id ever
     changes. */
  function mtWatchMobileSheetTitle() {
    var sheetRoot = document.getElementById('boost-pfs-filter-tree');
    if (!sheetRoot || sheetRoot.__mtTitleWatched) return;
    sheetRoot.__mtTitleWatched = true;
    function relabelTitleNode(el) {
      if (!el || !el.classList || !el.classList.contains('boost-pfs-filter-mobile-toolbar-middle')) return;
      var current = (el.textContent || '').replace(/\s+/g, ' ').trim();
      var mapped = MT_LABEL_MAP[current];
      if (mapped && el.textContent !== mapped) el.textContent = mapped;
    }
    relabelTitleNode(sheetRoot.querySelector('.boost-pfs-filter-mobile-toolbar-middle'));
    new MutationObserver(function (records) {
      records.forEach(function (r) {
        relabelTitleNode(r.type === 'characterData' ? r.target.parentElement : r.target);
        for (var i = 0; r.addedNodes && i < r.addedNodes.length; i++) relabelTitleNode(r.addedNodes[i]);
      });
    }).observe(sheetRoot, { subtree: true, childList: true, characterData: true });
  }

  /* ---------- custom price min/max inputs (2026-09-15, built+live-verified by subagent) ----------
     Boost's Price facet is 5 preset checkboxes with no slider. A Min–Max + Apply box is
     appended below the preset list, re-injected each render. API shape reverse-engineered
     live: FilterApi.setParam MUST get a one-element ARRAY (['25:50'] → pf_p_price[]=25:50,
     matching a real preset click) — a bare string is silently ignored server-side. The
     param key is hardcoded (paramKeyForSlug only resolves ALREADY-APPLIED params). */
  var MT_PRICE_PARAM_KEY = 'pf_p_price';

  function mtCurrentPriceRange() {
    var raw = toArray(getQueryParams()[MT_PRICE_PARAM_KEY])[0];
    if (!raw) return { min: '', max: '' };
    var m = String(raw).match(/^(\d*\.?\d*):(\d*\.?\d*)$/);
    if (!m) return { min: '', max: '' };
    return { min: m[1] || '', max: m[2] || '' };
  }

  function mtApplyPriceRange(minStr, maxStr) {
    /* empty min/max = open-ended bound, exactly how Boost's own presets express it
       ("Under $25" is literally ":25") */
    var rangeStr = minStr + ':' + maxStr;
    try {
      if (typeof window.FilterApi === 'undefined') throw new Error('no FilterApi');
      if (!minStr && !maxStr) {
        window.FilterApi.setParam(MT_PRICE_PARAM_KEY, []);
      } else {
        window.FilterApi.setParam(MT_PRICE_PARAM_KEY, [rangeStr]);
      }
      window.FilterApi.setParam('page', 1);
      window.FilterApi.applyFilter('filter', { filterOptionId: MT_PRICE_PARAM_KEY, filterValue: rangeStr });
      track('mt_price_custom_apply', { min: minStr, max: maxStr });
    } catch (e) {
      try {
        var url = new URL(location.href);
        if (!minStr && !maxStr) url.searchParams.delete(MT_PRICE_PARAM_KEY);
        else url.searchParams.set(MT_PRICE_PARAM_KEY, rangeStr);
        url.searchParams.set('page', '1');
        location.assign(url.toString());
      } catch (e2) { /* never break the UI */ }
    }
  }

  function mtWirePriceInputs(root) {
    root = root || document;
    var groups = root.querySelectorAll('.boost-pfs-filter-option-price');
    for (var gi = 0; gi < groups.length; gi++) {
      mtWireOnePriceGroup(groups[gi]);
    }
  }

  /* Separate function per group DELIBERATELY (mirrors mtWireFacetGroup): the Price facet
     renders in TWO trees at once on desktop (H bar + All Filters panel) — loop-scoped
     vars/closures would make the second instance silently capture the first's inputs
     (caught live: the H bar's Apply read the panel's empty inputs). */
  function mtWireOnePriceGroup(group) {
    var content = group.querySelector('.boost-pfs-filter-option-content');
    if (!content) return;
    var inner = content.querySelector('.boost-pfs-filter-option-content-inner') || content;
    var list = inner.querySelector('ul');
    var wrap = inner.querySelector(':scope > .mt-price-custom');
    if (wrap) return; /* idempotence within one render pass */

    wrap = document.createElement('div');
    wrap.className = 'mt-price-custom';
    var minInput = document.createElement('input');
    minInput.type = 'number';
    minInput.inputMode = 'decimal';
    minInput.min = '0';
    minInput.step = '1';
    minInput.placeholder = 'Min';
    minInput.className = 'mt-price-custom-input mt-price-custom-min';
    minInput.setAttribute('aria-label', 'Minimum price');
    var sep = document.createElement('span');
    sep.className = 'mt-price-custom-sep';
    sep.setAttribute('aria-hidden', 'true');
    sep.textContent = '–';
    var maxInput = document.createElement('input');
    maxInput.type = 'number';
    maxInput.inputMode = 'decimal';
    maxInput.min = '0';
    maxInput.step = '1';
    maxInput.placeholder = 'Max';
    maxInput.className = 'mt-price-custom-input mt-price-custom-max';
    maxInput.setAttribute('aria-label', 'Maximum price');
    var applyBtn = document.createElement('button');
    applyBtn.type = 'button';
    applyBtn.className = 'mt-price-custom-apply';
    applyBtn.textContent = 'Apply';

    var applied = mtCurrentPriceRange();
    minInput.value = applied.min;
    maxInput.value = applied.max;

    function doApply() {
      var minV = parseFloat(minInput.value);
      var maxV = parseFloat(maxInput.value);
      var hasMin = minInput.value !== '' && !isNaN(minV);
      var hasMax = maxInput.value !== '' && !isNaN(maxV);
      if (hasMin && hasMax && minV > maxV) {
        var t = minV; minV = maxV; maxV = t;
        minInput.value = String(minV);
        maxInput.value = String(maxV);
      }
      mtApplyPriceRange(hasMin ? String(minV) : '', hasMax ? String(maxV) : '');
    }

    wrap.addEventListener('click', function (e) { e.stopPropagation(); });
    [minInput, maxInput].forEach(function (inp) {
      inp.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          if (minInput.value || maxInput.value) {
            e.preventDefault();
            e.stopPropagation();
            minInput.value = '';
            maxInput.value = '';
          }
          return; /* empty-input Escape bubbles to close the panel */
        }
        e.stopPropagation();
        if (e.key === 'Enter') {
          e.preventDefault();
          doApply();
        }
      });
    });
    applyBtn.addEventListener('click', function (e) { e.stopPropagation(); doApply(); });

    var row = document.createElement('div');
    row.className = 'mt-price-custom-row';
    row.appendChild(minInput);
    row.appendChild(sep);
    row.appendChild(maxInput);
    row.appendChild(applyBtn);
    wrap.appendChild(row);
    inner.insertBefore(wrap, list ? list.nextSibling : null);
  }

  /* ---------- seasonal Hanukkah facet reorder, Boost H bar (2026-09-15) ----------
     Self-activating: handle matches /menorah|hanukkah/ AND date in Oct 1–Jan 5 (local
     time, year-wrap handled by the monthDay encoding) — reverts itself in January, no
     reminder needed. window.__mtSeasonTest (boolean) overrides the date for QA. Scope:
     H bar only; DOM insertBefore each render (Boost wipes the tree anyway). */
  function mtSeasonActive() {
    if (typeof window.__mtSeasonTest === 'boolean') return window.__mtSeasonTest;
    var d = new Date();
    var monthDay = (d.getMonth() + 1) * 100 + d.getDate();
    return monthDay >= 1001 || monthDay <= 105;
  }

  function mtSeasonalHandleMatches() {
    return /menorah|hanukkah/i.test(location.pathname);
  }

  function mtFindOptionByLabel(root, label) {
    var groups = root.querySelectorAll('.boost-pfs-filter-option');
    for (var i = 0; i < groups.length; i++) {
      var t = groups[i].querySelector('.boost-pfs-filter-option-title-text');
      var textNode = t && t.childNodes[0];
      var text = textNode && textNode.nodeType === 3 ? textNode.nodeValue : (t ? t.textContent : '');
      if ((text || '').replace(/\s+/g, ' ').trim() === label) return groups[i];
    }
    return null;
  }

  function mtSeasonalReorderBoost(root) {
    root = root || document;
    if (!mtSeasonalHandleMatches() || !mtSeasonActive()) return;
    var hbar = root.querySelector('.boost-pfs-filter-tree-h');
    if (!hbar) return;
    var wrapper = hbar.querySelector('.boost-pfs-filter-options-wrapper');
    if (!wrapper) return;
    var priceGroup = mtFindOptionByLabel(wrapper, 'Price');
    var typeGroup = mtFindOptionByLabel(wrapper, 'Shop by Type'); /* absent on some trees */
    if (priceGroup) wrapper.insertBefore(priceGroup, wrapper.firstChild);
    if (typeGroup) wrapper.insertBefore(typeGroup, wrapper.firstChild); /* ends up first */
  }

  /* ---------- mobile facet pills ---------- */
  function buildPills() {
    var host = document.getElementById('mt-filter-pills');
    var tree = document.getElementById('boost-pfs-filter-tree');
    if (!host || !tree) return;
    var groups = tree.querySelectorAll('.boost-pfs-filter-option');
    var params = getQueryParams();
    var html = '';
    var made = 0;
    for (var i = 0; i < groups.length; i++) {
      var g = groups[i];
      if (g.classList.contains('boost-pfs-filter-option-hidden')) continue;
      if (g.querySelector('.boost-pfs-filter-clear-all')) continue; // Boost's "Refine By" block, not a facet
      // The title/heading also carry selected-count spans; the clean facet name is the
      // first text node of the -title-text span.
      var titleEl = g.querySelector('.boost-pfs-filter-option-title-text')
        || g.querySelector('.boost-pfs-filter-option-title-heading')
        || g.querySelector('.boost-pfs-filter-option-title');
      var label = '';
      if (titleEl) {
        var textNode = titleEl.childNodes[0];
        label = (textNode && textNode.nodeType === 3 ? textNode.nodeValue : titleEl.textContent) || '';
        label = label.replace(/\s+/g, ' ').replace(/\(\d+\)/g, '').trim();
      }
      if (!label || /^refine by$/i.test(label)) continue;
      var slug = optionSlugFromClasses(g);
      var paramKey = paramKeyForSlug(slug);
      var count = paramKey ? toArray(params[paramKey]).length : 0;
      html += '<button type="button" class="mt-fv2-pill" data-mt-option="' + escapeHtml(slug) + '" aria-haspopup="dialog">'
        + escapeHtml(label)
        + (count ? '<span class="mt-fv2-pill-count">' + count + '</span>' : '')
        + '</button>';
      made++;
    }
    host.innerHTML = html;
  }

  /* ---------- applied-filter chips (mobile row + desktop row above grid) ---------- */
  function buildChips() {
    var params = getQueryParams();
    var entries = [];
    Object.keys(params).forEach(function (k) {
      if (k.indexOf('pf_') !== 0) return;
      toArray(params[k]).forEach(function (v) {
        entries.push({ key: k, value: String(v), label: formatValue(k, v) });
      });
    });
    var html = entries.map(function (en) {
      return '<button type="button" class="mt-fv2-chip" data-mt-key="' + escapeHtml(en.key)
        + '" data-mt-value="' + escapeHtml(en.value) + '" aria-label="Remove filter ' + escapeHtml(en.label) + '">'
        + escapeHtml(en.label) + '<span class="mt-fv2-chip-x" aria-hidden="true">&times;</span></button>';
    }).join('');
    if (entries.length > 1) {
      html += '<button type="button" class="mt-fv2-chip mt-fv2-chip-clear">Clear all</button>';
    }
    var topbar = document.querySelector('.mt-dgrid-topbar');
    ['mt-applied-filters', 'mt-applied-filters-desktop'].forEach(function (id) {
      var el = document.getElementById(id);
      if (!el) return;
      /* topbar mode retires the desktop chips row — applied state lives in the pills
         (counts + dark border) plus the Quince-style Clear All pill in the bar */
      var target = (topbar && id === 'mt-applied-filters-desktop') ? '' : html;
      if (el.innerHTML !== target) el.innerHTML = target;
    });
    syncClearAllPill(topbar, entries.length);
    /* applied-pill emphasis: the vendor count span exists on every option (empty until
       selected) — tag options that actually hold a selection */
    if (topbar) {
      topbar.querySelectorAll('.boost-pfs-filter-option').forEach(function (o) {
        var c = o.querySelector('.boost-pfs-filter-option-title-count');
        o.classList.toggle('mt-dgrid-pill-active', !!(c && c.textContent.trim()));
      });
    }
  }

  /* Quince's end-of-row "Clear All" pill: lives OUTSIDE the tree div (Boost wipes tree
     innerHTML each render), created once, toggled per render. */
  function syncClearAllPill(topbar, activeCount) {
    if (!topbar) return;
    var pill = topbar.querySelector('.mt-dgrid-clearall-pill');
    if (!pill) {
      var host = topbar.querySelector('.boost-pfs-container-default-box, .boost-pfs-container-default-fullwidth');
      var tree = host && host.querySelector('.boost-pfs-filter-tree-h');
      if (!host || !tree) return;
      pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'mt-dgrid-clearall-pill';
      pill.textContent = 'Clear All';
      pill.addEventListener('click', function (e) {
        /* the click must not reach Boost's document-level close-outside handler —
           with an open dropdown it auto-applies the still-checked values, instantly
           re-applying what we just cleared */
        e.stopPropagation();
        clearAllFilters();
      });
      host.insertBefore(pill, tree.nextSibling);
    }
    pill.hidden = activeCount === 0;
  }

  function removeFilterValue(key, value) {
    if (typeof window.FilterApi === 'undefined') return;
    var remaining = toArray(getQueryParams()[key]).filter(function (x) { return String(x) !== value; });
    window.FilterApi.setParam(key, remaining);
    window.FilterApi.setParam('page', 1);
    window.FilterApi.applyFilter('filter', { filterOptionId: key, filterValue: value });
    track('mt_fv2_chip_remove', { facet: key });
  }

  function clearAllFilters() {
    if (typeof window.FilterApi === 'undefined') return;
    var params = getQueryParams();
    Object.keys(params).forEach(function (k) {
      if (k.indexOf('pf_') === 0) window.FilterApi.setParam(k, []);
    });
    window.FilterApi.setParam('page', 1);
    window.FilterApi.applyFilter('filter');
    track('mt_fv2_chip_remove', { facet: 'all' });
  }

  /* ---------- bottom sheet ---------- */
  var tree = document.getElementById('boost-pfs-filter-tree');
  var backdrop = document.querySelector('.mt-fv2-backdrop');
  var sheetOpen = false;

  // Boost rebuilds the sheet header when the tree opens, resetting the title text —
  // re-patch on every render AND every open.
  function patchSheetChrome() {
    var sheetTitle = document.querySelector('.mt-fv2-tree .boost-pfs-filter-mobile-toolbar-middle');
    if (sheetTitle && /^refine by$/i.test(sheetTitle.textContent.trim())) {
      sheetTitle.textContent = 'Filters';
    }
    // Quince-style always-reachable "Clear all" in the pinned footer (Boost rebuilds the
    // footer on open, so re-inject whenever it's missing)
    var footer = document.querySelector('.mt-fv2-tree .boost-pfs-filter-mobile-footer');
    if (footer && !footer.querySelector('.mt-fv2-clear-link')) {
      var clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'mt-fv2-clear-link';
      clear.textContent = 'Clear all';
      footer.insertBefore(clear, footer.firstChild);
    }
  }

  function syncSheetState() {
    if (!tree) return;
    var open = tree.classList.contains('boost-pfs-filter-tree-mobile-open');
    if (open === sheetOpen) return;
    sheetOpen = open;
    if (backdrop) backdrop.hidden = !open;
    document.documentElement.classList.toggle('mt-fv2-sheet-open', open);
    if (open) {
      patchSheetChrome();
      setTimeout(patchSheetChrome, 250);
      track('mt_fv2_sheet_open', {});
    }
  }

  if (tree) {
    new MutationObserver(syncSheetState).observe(tree, { attributes: true, attributeFilter: ['class'] });
  }

  function openSheet(optId) {
    if (!tree) return;
    if (!tree.classList.contains('boost-pfs-filter-tree-mobile-open')) {
      // Boost binds its open logic to the injected button inside the mobile-button wrapper.
      var btn = document.querySelector('.boost-pfs-filter-tree-mobile-button button')
        || document.querySelector('.boost-pfs-filter-tree-mobile-button');
      if (btn) btn.click();
    }
    if (!optId) return;
    // Boost re-renders while the sheet opens, so retry the expand until the
    // -content-open class sticks (style2 expands via the heading button).
    var attempts = 0;
    (function expand() {
      attempts++;
      var group = tree.querySelector('.boost-pfs-filter-option-' + optId);
      var content = group && group.querySelector('.boost-pfs-filter-option-content');
      if (content && !content.classList.contains('boost-pfs-filter-option-content-open')) {
        var t = group.querySelector('.boost-pfs-filter-option-title-heading');
        if (t) t.click();
      }
      if (content && content.classList.contains('boost-pfs-filter-option-content-open')) {
        group.scrollIntoView({ block: 'start' });
      } else if (attempts < 4) {
        setTimeout(expand, 300);
      }
    })();
  }

  function closeSheet() {
    if (!tree) return;
    var closeBtn = tree.querySelector('.boost-pfs-filter-show-result')
      || tree.querySelector('.boost-pfs-filter-close');
    if (closeBtn) { closeBtn.click(); return; }
    tree.classList.remove('boost-pfs-filter-tree-mobile-open');
    document.body.classList.remove('boost-pfs-filter-tree-open-body');
  }

  /* ---------- sort bottom sheet (Boost's dropdown gets clipped by the pill rail) ---------- */
  var sortSheet = null;
  var sortSheetOpen = false;

  function ensureSortSheet() {
    if (sortSheet) return sortSheet;
    sortSheet = document.createElement('div');
    sortSheet.className = 'mt-fv2-sort-sheet';
    sortSheet.setAttribute('role', 'listbox');
    sortSheet.hidden = true;
    document.body.appendChild(sortSheet);
    sortSheet.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('.mt-fv2-sort-close')) { closeSortSheet(); return; }
      var row = e.target.closest && e.target.closest('.mt-fv2-sort-option');
      if (!row) return;
      // hand the choice to Boost's own (hidden) dropdown anchor so its engine applies it
      var anchors = document.querySelectorAll('.mt-fv2-sort .boost-pfs-filter-filter-dropdown a');
      for (var i = 0; i < anchors.length; i++) {
        if ((anchors[i].getAttribute('data-sort') || '') === row.getAttribute('data-mt-sort')) {
          anchors[i].click();
          break;
        }
      }
      track('mt_fv2_sort', { value: row.getAttribute('data-mt-sort') });
      closeSortSheet();
    });
    return sortSheet;
  }

  function openSortSheet() {
    var anchors = document.querySelectorAll('.mt-fv2-sort .boost-pfs-filter-filter-dropdown a');
    if (!anchors.length) return;
    var btn = document.querySelector('.mt-fv2-sort .boost-pfs-filter-top-sorting-wrapper');
    var currentLabel = btn ? btn.textContent.replace(/\s+/g, ' ').trim() : '';
    var html = '<button type="button" class="mt-fv2-sort-close" aria-label="Close">&times;</button>'
      + '<div class="mt-fv2-sort-sheet-title">Sort by</div>';
    for (var i = 0; i < anchors.length; i++) {
      var label = anchors[i].textContent.replace(/\s+/g, ' ').trim();
      var val = anchors[i].getAttribute('data-sort') || '';
      var selected = label === currentLabel;
      html += '<button type="button" role="option" class="mt-fv2-sort-option" data-mt-sort="' + escapeHtml(val) + '"'
        + ' aria-checked="' + (selected ? 'true' : 'false') + '">' + escapeHtml(label) + '</button>';
    }
    var sheet = ensureSortSheet();
    sheet.innerHTML = html;
    sheet.hidden = false;
    sortSheetOpen = true;
    if (backdrop) backdrop.hidden = false;
  }

  function closeSortSheet() {
    if (sortSheet) sortSheet.hidden = true;
    sortSheetOpen = false;
    if (backdrop && !sheetOpen) backdrop.hidden = true;
  }

  // Capture-phase intercept: on mobile the sort pill opens our sheet, never Boost's
  // dropdown. Clicks on the (hidden) dropdown anchors must pass through — that's how the
  // sort sheet forwards the chosen option to Boost.
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('.boost-pfs-filter-filter-dropdown')) return;
    if (e.target.closest('.mt-fv2-sort') && window.matchMedia('(max-width: 767.98px)').matches) {
      e.preventDefault();
      e.stopPropagation();
      openSortSheet();
    }
  }, true);

  if (backdrop) backdrop.addEventListener('click', function () {
    if (sortSheetOpen) { closeSortSheet(); return; }
    closeSheet();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (sortSheetOpen) { closeSortSheet(); return; }
    if (sheetOpen) closeSheet();
  });

  /* ---------- delegated clicks ---------- */
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    var pill = e.target.closest('.mt-fv2-pill');
    if (pill) {
      track('mt_fv2_pill_tap', { facet: pill.textContent.trim() });
      openSheet(pill.getAttribute('data-mt-option'));
      return;
    }
    var clear = e.target.closest('.mt-fv2-chip-clear, .mt-fv2-clear-link');
    if (clear) { clearAllFilters(); return; }

    var chip = e.target.closest('.mt-fv2-chip');
    if (chip) {
      removeFilterValue(chip.getAttribute('data-mt-key'), chip.getAttribute('data-mt-value'));
      return;
    }
    var subcat = e.target.closest('.mt-subcat__chip');
    if (subcat) {
      track('mt_fv2_subcat_click', { href: subcat.getAttribute('href') || '' });
      return;
    }
    var optItem = e.target.closest('.boost-pfs-filter-option-item');
    if (optItem && e.target.closest('.boost-pfs-filter-tree')) {
      var group = optItem.closest('.boost-pfs-filter-option');
      track('mt_fv2_filter_apply', { facet: group ? optionSlugFromClasses(group) : '' });
      return;
    }
    if (e.target.closest('.boost-pfs-filter-custom-sorting')) {
      track('mt_fv2_sort', {});
    }
  });

  /* ---------- post-render work (pills, chips, labels) ---------- */
  // The theme header is sticky (z-index 600, ~49px on mobile); the v2 toolbar sticks
  // directly beneath it. Measured at runtime because the header height can change.
  function setHeaderOffset() {
    var header = document.querySelector('#shopify-section-header-top');
    var h = header ? Math.round(header.getBoundingClientRect().height) : 49;
    document.documentElement.style.setProperty('--mt-fv2-top', h + 'px');
  }
  setHeaderOffset();
  window.addEventListener('resize', setHeaderOffset);

  function a11yPass() {
    /* (audit 2026-09-15) Boost sets aria-checked only on SELECTED options and removes
       the attribute entirely when unchecked, so unchecked checkboxes expose no state
       (axe aria-required-attr, critical). Default missing ones to "false" — the
       vendor's own attr(true) on selection still wins. */
    document.querySelectorAll('.boost-pfs-filter-button[role="checkbox"]:not([aria-checked])')
      .forEach(function (b) { b.setAttribute('aria-checked', 'false'); });
    /* the inner glyph span duplicates role=checkbox + tabindex (nested-interactive,
       doubled tab stops) and carries an aria-labelledby pointing at a non-existent id —
       make it purely decorative; the outer <button> stays the one operable control */
    document.querySelectorAll('.boost-pfs-filter-button [role="checkbox"]')
      .forEach(function (s) {
        s.removeAttribute('role');
        s.removeAttribute('tabindex');
        s.removeAttribute('aria-labelledby');
        s.setAttribute('aria-hidden', 'true');
      });
    /* announce the updated result count after applies (WCAG 4.1.3) — Boost re-renders
       the element every pass, so re-apply each time */
    document.querySelectorAll('.boost-pfs-filter-total-product').forEach(function (el) {
      if (!el.hasAttribute('aria-live')) el.setAttribute('aria-live', 'polite');
    });
  }

  function onBoostRender() {
    mtRelabelFacets(document); /* MUST run before buildPills — pills read title text */
    buildPills();
    buildChips();
    a11yPass();
    mtAddFacetSearch(document);
    mtWirePriceInputs(document);
    mtSeasonalReorderBoost(document);

    // Belt-and-braces: if the label config didn't reach Boost's Labels, patch the button
    // text and the sheet-header title.
    var mobileBtn = document.querySelector('.boost-pfs-filter-tree-mobile-button button');
    if (mobileBtn && /^(refine by|all filters)$/i.test(mobileBtn.textContent.trim())) {
      mobileBtn.textContent = 'Filter';
    }
    patchSheetChrome();

    // Desktop expand/collapse states are left to Boost's own settings — an auto-collapse
    // pass here raced Boost's active-filter auto-expand and produced inconsistent states.
  }

  function wirePrototypes() {
    if (typeof window.Filter === 'undefined' || !window.Filter.prototype) return false;
    var origAfterRender = window.Filter.prototype.afterRender;
    window.Filter.prototype.afterRender = function (data) {
      if (origAfterRender) origAfterRender.call(this, data);
      try { onBoostRender(data || this.data); } catch (err) { /* never break Boost's render */ }
    };
    return true;
  }

  // Defer order guarantees boost-pfs-filter.js already ran, but guard anyway.
  if (!wirePrototypes()) {
    document.addEventListener('DOMContentLoaded', wirePrototypes);
    window.addEventListener('load', wirePrototypes);
  }

  /* ---------- session tagging ---------- */
  try { if (typeof window.clarity === 'function') window.clarity('set', 'mt_filter_test', 'b'); } catch (e) { }
  track('mt_fv2_pageview', { page_type: /\/search/.test(location.pathname) ? 'search' : 'collection' });
})();

/* DESKTOP GRID 2026 topbar (2026-08-04): All Filters slide-over toggle. The panel markup
   only exists on topbar variant templates (dg_filter_layout == 'topbar') — this file also
   loads on the default template, so everything is guarded on the panel's presence. The
   panel hosts a third Boost tree (vertical, data-is-desktop) that the vendor keeps in
   sync with the pill bar and mobile sheet on every render. */
(function () {
  'use strict';
  var panel = document.getElementById('mt-dgrid-allfilters-panel');
  var bar = document.querySelector('.mt-dgrid-topbar');
  if (!panel || !bar) return;
  var btn = bar.querySelector('.mt-dgrid-allfilters-btn');
  var backdrop = document.querySelector('.mt-dgrid-allfilters-backdrop');
  var closeBtn = panel.querySelector('.mt-dgrid-allfilters-close');
  /* a11y (audit 2026-09-15): dialog semantics + focus moves in on open, back to the
     trigger on close (WCAG 2.4.3 / 4.1.2) */
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', 'All filters');
  function openPanel() {
    panel.classList.add('is-open');
    panel.setAttribute('aria-modal', 'true');
    if (backdrop) backdrop.hidden = false;
    if (btn) btn.setAttribute('aria-expanded', 'true');
    var f = closeBtn || panel.querySelector('button, input, [tabindex="0"]');
    if (f) { try { f.focus(); } catch (e) { } }
  }
  function closePanel() {
    panel.classList.remove('is-open');
    panel.removeAttribute('aria-modal');
    if (backdrop) backdrop.hidden = true;
    if (btn) {
      btn.setAttribute('aria-expanded', 'false');
      try { btn.focus(); } catch (e) { }
    }
  }
  if (btn) btn.addEventListener('click', function () {
    if (panel.classList.contains('is-open')) { closePanel(); } else { openPanel(); }
  });
  if (closeBtn) closeBtn.addEventListener('click', closePanel);
  if (backdrop) backdrop.addEventListener('click', closePanel);
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    /* the pill dropdowns ignored Escape while every other overlay honored it (audit
       2026-09-15) — close an open one via its own title toggle (instant-apply mode:
       nothing pending to mis-apply). aria-expanded lives on the heading BUTTON, not
       the -title wrapper (first fix attempt queried the wrapper and matched nothing). */
    var openTitle = document.querySelector(
      '.boost-pfs-filter-tree-h .boost-pfs-filter-option-title-heading[aria-expanded="true"], ' +
      '.boost-pfs-filter-tree-h .boost-pfs-filter-option-title [aria-expanded="true"], ' +
      '.boost-pfs-filter-tree-h .boost-pfs-filter-option-title[aria-expanded="true"]');
    if (openTitle) { openTitle.click(); try { openTitle.focus(); } catch (err) { } return; }
    if (panel.classList.contains('is-open')) closePanel();
  });
})();

/* Pinned filter bar on scroll (Quince): sticky is dead under the theme's
   overflow:hidden main wrapper — toggle fixed positioning + a spacer instead. */
(function () {
  'use strict';
  var bar = document.querySelector('.mt-dgrid-topbar');
  if (!bar || !window.matchMedia('(min-width: 768px)').matches) return;
  var spacer = document.createElement('div');
  spacer.style.display = 'none';
  bar.parentNode.insertBefore(spacer, bar.nextSibling);
  /* pin offset = live bottom of the theme's stacked sticky header sections
     (top strip + nav; ~123px on desktop, taller than the mobile-oriented CSS var) */
  function headerH() {
    var t = 0;
    ['#shopify-section-header-top', '#shopify-section-header'].forEach(function (sel) {
      var el = document.querySelector(sel);
      if (!el) return;
      var cs = getComputedStyle(el);
      if (cs.position === 'sticky' || cs.position === 'fixed') {
        var bRect = el.getBoundingClientRect().bottom;
        if (bRect > t) t = bRect;
      }
    });
    return t;
  }
  /* Fully LIVE pinning — no cached threshold, no frozen top offset. A fast scroll
     during page load used to capture the header's bottom mid-flight (announcement
     bars still in view, images still shifting layout) and freeze the bar with a gap
     below the header. Now every scroll frame re-reads the real geometry: the bar
     sticks when its natural position reaches the header, the pinned top self-corrects
     each frame, and the spacer (which holds the bar's natural slot) drives unsticking. */
  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      var h = headerH();
      if (!bar.classList.contains('mt-stuck')) {
        if (bar.getBoundingClientRect().top <= h - 2) {
          spacer.style.height = bar.offsetHeight + 'px';
          spacer.style.display = 'block';
          /* tuck 4px up under the header's bottom padding — tighter join, no pill
             clipping (the bar's own top padding absorbs the overlap) */
          bar.style.top = (h - 4) + 'px';
          bar.classList.add('mt-stuck');
        }
      } else {
        bar.style.top = (h - 4) + 'px';
        if (spacer.getBoundingClientRect().top > h - 2) {
          bar.classList.remove('mt-stuck');
          bar.style.top = '';
          spacer.style.display = 'none';
        }
      }
    });
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  window.addEventListener('load', onScroll);
  setTimeout(onScroll, 400); /* browser-restored scroll positions pin immediately */
})();
